import {
  TEARDOWN,
  SESSION_STARTED,
  SESSION_STOPPED,
  GATEWAY_STATE,
  SIP_STATE,
  PRESENCE_UPDATE,
  CALL_DIALING,
  CALL_ANSWERED,
  HANGUP,
  CALL_ENDED,
  MUTE_CHANGED,
  NOTIFY,
} from './actions'
import { dialTarget } from './lib/helpers'

export const initialState = {
  // true while a voice session runs (between SESSION_STARTED and its end)
  active: false,
  // why the last session stopped by itself:
  // null | unauthorized | notPermitted | error
  stopReason: null,

  gatewayState: 'idle', // idle | connecting | connected | retrying
  sipState: 'idle', // idle | connecting | registered | retrying | failed
  sipError: null,

  // gateway presence:
  // [{ vehicle_id, imei, sip_user, code, plate, state, call_id, last_seen }]
  // sip_user is missing from an older gateway, null for a vehicle that has no
  // SIP user (see dialTarget).
  vehicles: [],

  // { id, vehicleId, imei, sipUser, code, plate, state: dialing | active,
  //   startedAt, answeredAt, hangingUp, callId }
  // sipUser is the SIP user dialled (dialTarget of the vehicle).
  activeCall: null,
  mute: false,
  // { cause, at, code, plate } of the last call that ended
  lastCallEnd: null,

  // { id, level, key, hintKey, detail }
  notice: null,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case TEARDOWN:
      return initialState

    case SESSION_STARTED:
      return {
        ...initialState,
        active: true,
        gatewayState: 'connecting',
        sipState: 'connecting',
        notice: state.notice,
      }

    case SESSION_STOPPED:
      return {
        ...state,
        active: false,
        stopReason: action.payload.reason || null,
        gatewayState: 'idle',
        sipState: 'idle',
        sipError: null,
        vehicles: [],
        activeCall: null,
        mute: false,
      }

    case GATEWAY_STATE:
      return { ...state, gatewayState: action.payload.state }

    case SIP_STATE:
      return {
        ...state,
        sipState: action.payload.state,
        sipError: action.payload.error || null,
      }

    case PRESENCE_UPDATE:
      return {
        ...state,
        vehicles: Array.isArray(action.payload.vehicles)
          ? action.payload.vehicles
          : [],
      }

    case CALL_DIALING: {
      const { vehicle, id, at } = action.payload
      return {
        ...state,
        activeCall: {
          id,
          vehicleId:
            vehicle.vehicle_id === undefined ? null : vehicle.vehicle_id,
          imei: vehicle.imei,
          sipUser: dialTarget(vehicle),
          code: vehicle.code,
          plate: vehicle.plate,
          state: 'dialing',
          startedAt: at,
          answeredAt: null,
          hangingUp: false,
          // Outbound calls get no gateway call id; see shouldHandleGatewayHangup.
          callId: null,
        },
        mute: false,
      }
    }

    case CALL_ANSWERED:
      if (!state.activeCall || state.activeCall.state !== 'dialing') {
        return state
      }
      return {
        ...state,
        activeCall: {
          ...state.activeCall,
          state: 'active',
          answeredAt: action.payload.at,
        },
      }

    case HANGUP:
      if (!state.activeCall) return state
      return { ...state, activeCall: { ...state.activeCall, hangingUp: true } }

    case CALL_ENDED:
      // Idempotent: SIP and the dial error path can both report one end.
      if (!state.activeCall) return state
      return {
        ...state,
        activeCall: null,
        mute: false,
        lastCallEnd: {
          cause: action.payload.cause || null,
          at: action.payload.at,
          code: state.activeCall.code,
          plate: state.activeCall.plate,
        },
      }

    case MUTE_CHANGED:
      return state.activeCall
        ? { ...state, mute: !!action.payload.muted }
        : state

    case NOTIFY:
      return { ...state, notice: action.payload }

    default:
      return state
  }
}
